import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneLight } from "react-syntax-highlighter/dist/esm/styles/prism";
import {
  Play,
  Copy,
  Check,
  Loader2,
  Code2,
  FileText,
  Image as ImageIcon,
  ListFilter,
  Sparkles,
  Clock,
  Coins,
  Hash,
  History,
  ChevronRight,
  Trash2,
  RotateCcw,
  X
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Separator } from "@/components/ui/separator";

const TASK_TYPES = [
  { value: "text", label: "Text", icon: FileText },
  { value: "code", label: "Code", icon: Code2 },
  { value: "summarize", label: "Summarize", icon: ListFilter },
  { value: "image", label: "Image", icon: ImageIcon },
];

export default function PlaygroundPage({ providers, api }) {
  const [provider, setProvider] = useState("");
  const [taskType, setTaskType] = useState("text");
  const [model, setModel] = useState("");
  const [prompt, setPrompt] = useState("");
  const [temperature, setTemperature] = useState([0.7]);
  const [maxTokens, setMaxTokens] = useState(2048);
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState(null);
  const [copied, setCopied] = useState(false);
  const [copiedRequest, setCopiedRequest] = useState(false);
  const [streamMode, setStreamMode] = useState(true);
  const [streamingText, setStreamingText] = useState("");
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [activeSessionId, setActiveSessionId] = useState(null);
  const responseEndRef = useRef(null);

  // Auto-scroll response as streaming content arrives
  useEffect(() => {
    if (streamingText && responseEndRef.current) {
      responseEndRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  }, [streamingText]);

  // Fetch history
  const fetchHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const res = await axios.get(`${api}/playground/history?limit=50`);
      setHistory(res.data || []);
    } catch (err) {
      console.error("Failed to load history:", err);
    } finally {
      setHistoryLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const loadSession = (session) => {
    setProvider(session.provider);
    setTaskType(session.task_type);
    setModel(session.model);
    setPrompt(session.prompt || "");
    setTemperature([session.temperature || 0.7]);
    setMaxTokens(session.max_tokens || 2048);
    setResponse({
      output: session.output,
      images: session.images || [],
      usage: session.usage || {},
      estimated_cost: session.estimated_cost || 0,
      latency: session.latency || 0,
      status: session.status || "success",
    });
    setActiveSessionId(session.id);
    setHistoryOpen(false);
  };

  const deleteSession = async (e, sessionId) => {
    e.stopPropagation();
    try {
      await axios.delete(`${api}/playground/history/${sessionId}`);
      setHistory(prev => prev.filter(s => s.id !== sessionId));
      if (activeSessionId === sessionId) setActiveSessionId(null);
    } catch { toast.error("Failed to delete"); }
  };

  const clearAllHistory = async () => {
    try {
      await axios.delete(`${api}/playground/history`);
      setHistory([]);
      toast.success("History cleared");
    } catch { toast.error("Failed to clear"); }
  };

  // Available providers (those with keys)
  const availableProviders = providers.filter((p) => p.key_status);

  // Sort by name
  const sortedProviders = [...availableProviders].sort((a, b) => {
    return a.name.localeCompare(b.name);
  });

  // Available models for selected provider/task
  const currentProvider = providers.find((p) => p.slug === provider);
  const availableModels = currentProvider?.models?.[taskType] || [];

  // Reset model when provider or task changes
  useEffect(() => {
    if (availableModels.length > 0) {
      setModel(availableModels[0].id);
    } else {
      setModel("");
    }
  }, [provider, taskType]);

  // Check if task type is supported
  const isTaskSupported = currentProvider?.capabilities?.includes(taskType);

  const handleRun = async () => {
    if (!provider || !model || !prompt.trim()) {
      toast.error("Please fill in all fields");
      return;
    }
    if (!isTaskSupported) {
      toast.error(`${currentProvider?.name} does not support ${taskType} tasks`);
      return;
    }

    setLoading(true);
    setResponse(null);
    setStreamingText("");

    const requestBody = {
      provider,
      model,
      task_type: taskType,
      prompt: prompt.trim(),
      temperature: temperature[0],
      max_tokens: maxTokens,
    };

    // Use streaming for text/code/summarize tasks
    if (streamMode && taskType !== "image") {
      try {
        const res = await fetch(`${api}/llm/stream`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("llm_hub_token")}`,
          },
          body: JSON.stringify(requestBody),
        });

        if (!res.ok) {
          const errData = await res.json().catch(() => ({}));
          throw new Error(errData.detail || "Stream request failed");
        }

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let fullText = "";
        let doneData = null;

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const text = decoder.decode(value, { stream: true });
          const lines = text.split("\n");

          for (const line of lines) {
            if (line.startsWith("data: ")) {
              try {
                const data = JSON.parse(line.slice(6));
                if (data.type === "chunk") {
                  fullText += data.content;
                  setStreamingText(fullText);
                } else if (data.type === "done") {
                  doneData = data;
                } else if (data.type === "error") {
                  throw new Error(data.content);
                }
              } catch (parseErr) {
                if (parseErr.message !== "Unexpected end of JSON input") {
                  // Ignore partial JSON parse errors from chunked transfer
                }
              }
            }
          }
        }

        setResponse({
          output: fullText,
          usage: doneData?.usage || { total_tokens: 0 },
          estimated_cost: doneData?.estimated_cost || 0,
          latency: doneData?.latency || 0,
          status: doneData?.status || "success",
          budget_warning: doneData?.budget_warning,
        });
        setStreamingText("");
        toast.success("Generation complete!");
        fetchHistory();
      } catch (err) {
        const detail = err.message || "Stream failed";
        toast.error(detail);
        setResponse({ status: "error", output: detail, usage: {}, estimated_cost: 0, latency: 0 });
        setStreamingText("");
      } finally {
        setLoading(false);
      }
    } else {
      // Non-streaming fallback (images, or stream mode off)
      try {
        const res = await axios.post(`${api}/llm/generate`, requestBody);
        setResponse(res.data);
        if (res.data.budget_warning) {
          toast.warning(res.data.budget_warning.message);
        }
        toast.success("Generation complete!");
        fetchHistory();
      } catch (err) {
        const detail = err.response?.data?.detail || "Generation failed";
        toast.error(detail);
        setResponse({ status: "error", output: detail, usage: {}, estimated_cost: 0, latency: 0 });
      } finally {
        setLoading(false);
      }
    }
  };

  const safeCopy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.style.position = "fixed";
      textarea.style.left = "-9999px";
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
    }
  };

  const handleCopyResponse = async () => {
    if (response?.output) {
      await safeCopy(response.output);
      setCopied(true);
      toast.success("Response copied!");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleCopyRequest = async () => {
    const curlCmd = `curl -X POST ${api}/llm/generate \\
  -H "Content-Type: application/json" \\
  -d '${JSON.stringify({
    provider,
    model,
    task_type: taskType,
    prompt: prompt.trim(),
    temperature: temperature[0],
    max_tokens: maxTokens,
  }, null, 2)}'`;
    await safeCopy(curlCmd);
    setCopiedRequest(true);
    toast.success("cURL command copied!");
    setTimeout(() => setCopiedRequest(false), 2000);
  };

  const detectLanguage = (text) => {
    if (!text) return "text";
    if (text.includes("```python") || text.includes("def ") || text.includes("import ")) return "python";
    if (text.includes("```javascript") || text.includes("function ") || text.includes("const ")) return "javascript";
    if (text.includes("```html") || text.includes("<html") || text.includes("<div")) return "html";
    if (text.includes("```css") || text.includes("{") && text.includes(":") && text.includes(";")) return "css";
    if (text.includes("```json")) return "json";
    return "text";
  };

  return (
    <div className="main-content-inner">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Playground</h1>
          <p className="text-sm text-muted-foreground mt-1">Test prompts across any LLM provider</p>
        </div>
        <Button
          variant={historyOpen ? "default" : "outline"}
          size="sm"
          onClick={() => { setHistoryOpen(!historyOpen); if (!historyOpen) fetchHistory(); }}
          className="gap-1.5"
          data-testid="playground-history-btn"
        >
          <History className="w-4 h-4" />
          History
          {history.length > 0 && (
            <Badge variant="secondary" className="ml-1 text-[10px] px-1.5 h-4">{history.length}</Badge>
          )}
        </Button>
      </div>

      {/* History Panel */}
      {historyOpen && (
        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="mb-6">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm flex items-center gap-2">
                  <History className="w-4 h-4" />
                  Generation History
                </CardTitle>
                <div className="flex items-center gap-2">
                  {history.length > 0 && (
                    <Button variant="ghost" size="sm" className="text-xs text-destructive h-7" onClick={clearAllHistory}>
                      <Trash2 className="w-3 h-3 mr-1" /> Clear All
                    </Button>
                  )}
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setHistoryOpen(false)}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              {historyLoading ? (
                <div className="flex items-center justify-center py-6"><Loader2 className="w-5 h-5 animate-spin" /></div>
              ) : history.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-6">No history yet. Run a prompt to get started.</p>
              ) : (
                <div className="max-h-[300px] overflow-y-auto space-y-1 pr-1">
                  {history.map((s) => (
                    <div
                      key={s.id}
                      className={`flex items-start gap-3 p-2.5 rounded-lg border cursor-pointer transition-colors hover:bg-muted/60 ${activeSessionId === s.id ? "bg-primary/5 border-primary/30" : "border-transparent"}`}
                      onClick={() => loadSession(s)}
                      data-testid="history-item"
                    >
                      <div className="w-8 h-8 rounded-md flex items-center justify-center shrink-0 text-white text-[10px] font-bold mt-0.5"
                        style={{ backgroundColor: providers.find(p => p.slug === s.provider)?.accent || "#666" }}>
                        {s.provider?.slice(0, 2).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-medium truncate">{s.model}</span>
                          <Badge variant="outline" className="text-[9px] shrink-0">{s.task_type}</Badge>
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{s.prompt}</p>
                        <div className="flex items-center gap-3 mt-1 text-[10px] text-muted-foreground">
                          <span>{s.usage?.total_tokens || 0} tokens</span>
                          <span>${s.estimated_cost?.toFixed(6)}</span>
                          <span>{s.latency?.toFixed(1)}s</span>
                          <span>{new Date(s.timestamp).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-0.5 shrink-0">
                        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={(e) => { e.stopPropagation(); loadSession(s); }} title="Load">
                          <RotateCcw className="w-3 h-3" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-6 w-6 text-destructive" onClick={(e) => deleteSession(e, s.id)} title="Delete">
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      )}

      <div className="space-y-6">
        {/* Response Viewer — on top, auto-expanding */}
        <Card data-testid="playground-response-panel">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-semibold tracking-tight">Response</CardTitle>
              {response && response.status !== "error" && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleCopyResponse}
                  data-testid="playground-response-copy-button"
                  className="h-7 text-xs gap-1"
                >
                  {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                  Copy
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {loading && !streamingText && (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-3" />
                  <p className="text-sm text-muted-foreground">Generating response...</p>
                </div>
              </div>
            )}

            {loading && streamingText && (
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin text-primary" />
                  <span className="text-xs text-muted-foreground">Streaming...</span>
                </div>
                <Separator />
                <div className="rounded-md">
                  <div className="response-area whitespace-pre-wrap text-sm leading-relaxed pr-2">
                    {streamingText}
                    <span className="inline-block w-2 h-4 bg-primary animate-pulse ml-0.5" />
                  </div>
                  <div ref={responseEndRef} />
                </div>
              </div>
            )}

            {!loading && !response && !streamingText && (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <Sparkles className="w-10 h-10 text-muted-foreground mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">Run a prompt to see the response here.</p>
                </div>
              </div>
            )}

            {!loading && response && (
              <div className="space-y-4">
                {/* Meta row */}
                <div className="flex flex-wrap gap-3" data-testid="playground-response-meta">
                  {response.latency > 0 && (
                    <Badge variant="secondary" className="text-xs gap-1">
                      <Clock className="w-3 h-3" /> {response.latency}s
                    </Badge>
                  )}
                  {response.usage?.total_tokens > 0 && (
                    <Badge variant="secondary" className="text-xs gap-1">
                      <Hash className="w-3 h-3" /> {response.usage.total_tokens} tokens
                    </Badge>
                  )}
                  {response.estimated_cost > 0 && (
                    <Badge variant="secondary" className="text-xs gap-1">
                      <Coins className="w-3 h-3" /> ${response.estimated_cost.toFixed(6)} (₹{(response.estimated_cost * 85.5).toFixed(2)})
                    </Badge>
                  )}
                  <Badge
                    variant={response.status === "success" ? "secondary" : "destructive"}
                    className="text-xs"
                  >
                    {response.status}
                  </Badge>
                </div>

                <Separator />

                {/* Response content — no max height, auto expands */}
                <div className="rounded-md">
                  {response.images && response.images.length > 0 ? (
                    <div className="space-y-3">
                      {response.images.map((url, i) => (
                        <img
                          key={i}
                          src={url}
                          alt={`Generated ${i + 1}`}
                          className="rounded-lg max-w-full"
                        />
                      ))}
                    </div>
                  ) : taskType === "code" || detectLanguage(response.output) !== "text" ? (
                    <SyntaxHighlighter
                      language={detectLanguage(response.output)}
                      style={oneLight}
                      customStyle={{
                        borderRadius: 8,
                        fontSize: 13,
                        fontFamily: "var(--font-mono)",
                        margin: 0,
                      }}
                      wrapLongLines
                    >
                      {response.output || ""}
                    </SyntaxHighlighter>
                  ) : (
                    <div className="response-area whitespace-pre-wrap text-sm leading-relaxed pr-2">
                      {response.output}
                    </div>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Prompt Composer — below response */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold tracking-tight">Prompt</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Provider */}
            <div className="space-y-2">
              <Label htmlFor="playground-provider">Provider</Label>
              <Select value={provider} onValueChange={setProvider} data-testid="playground-provider-select">
                <SelectTrigger id="playground-provider">
                  <SelectValue placeholder="Select a provider" />
                </SelectTrigger>
                <SelectContent>
                  {sortedProviders.length === 0 ? (
                    <SelectItem value="none" disabled>No keys configured</SelectItem>
                  ) : (
                    sortedProviders.map((p) => (
                      <SelectItem key={p.slug} value={p.slug}>
                        <span className="flex items-center gap-2">
                          {p.name}
                          {p.source === "marketplace" && (
                            <span className="text-[10px] font-semibold bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded-full">PLUGIN</span>
                          )}
                        </span>
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              {providers.length > 0 && availableProviders.length === 0 && (
                <p className="text-xs text-amber-600">Add API keys first to use the playground.</p>
              )}
            </div>

            {/* Task Type */}
            <div className="space-y-2">
              <Label>Task Type</Label>
              <Tabs value={taskType} onValueChange={setTaskType} data-testid="playground-task-tabs">
                <TabsList className="grid grid-cols-4 w-full">
                  {TASK_TYPES.map((t) => (
                    <TabsTrigger key={t.value} value={t.value} className="text-xs gap-1">
                      <t.icon className="w-3 h-3" />
                      {t.label}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>
              {provider && !isTaskSupported && (
                <p className="text-xs text-red-500">{currentProvider?.name} doesn't support {taskType} tasks.</p>
              )}
            </div>

            {/* Model */}
            <div className="space-y-2">
              <Label htmlFor="playground-model">Model</Label>
              <Select value={model} onValueChange={setModel} data-testid="playground-model-select">
                <SelectTrigger id="playground-model">
                  <SelectValue placeholder="Select a model" />
                </SelectTrigger>
                <SelectContent>
                  {availableModels.map((m) => (
                    <SelectItem key={m.id} value={m.id}>
                      <span className="flex items-center gap-2">
                        <span>{m.name}</span>
                        {m.input_cost === 0 && m.output_cost === 0 ? (
                          <span className="text-[10px] font-semibold bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full">FREE</span>
                        ) : (
                          <span className="text-[10px] text-muted-foreground">
                            {m.cost_per_image ? `$${m.cost_per_image}/img` : `$${m.input_cost}/$${m.output_cost} per 1M tok`}
                          </span>
                        )}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Temperature */}
            {taskType !== "image" && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Temperature</Label>
                  <span className="text-xs font-mono text-muted-foreground">{temperature[0].toFixed(1)}</span>
                </div>
                <Slider
                  value={temperature}
                  onValueChange={setTemperature}
                  min={0}
                  max={2}
                  step={0.1}
                  className="w-full"
                />
              </div>
            )}

            {/* Stream Toggle */}
            {taskType !== "image" && (
              <div className="flex items-center justify-between">
                <Label className="text-xs">Streaming Mode</Label>
                <button
                  data-testid="stream-toggle"
                  onClick={() => setStreamMode(!streamMode)}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${streamMode ? 'bg-primary' : 'bg-muted'}`}
                >
                  <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${streamMode ? 'translate-x-4' : 'translate-x-0.5'}`} />
                </button>
              </div>
            )}

            {/* Prompt */}
            <div className="space-y-2">
              <Label htmlFor="playground-prompt">Prompt</Label>
              <Textarea
                id="playground-prompt"
                data-testid="playground-prompt-textarea"
                placeholder={
                  taskType === "code"
                    ? "Write a Python function that..."
                    : taskType === "summarize"
                    ? "Paste the text you want to summarize..."
                    : taskType === "image"
                    ? "Describe the image you want to generate..."
                    : "Ask anything..."
                }
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                className="min-h-[140px] resize-y"
              />
            </div>

            {/* Actions */}
            <div className="flex gap-2">
              <Button
                onClick={handleRun}
                disabled={loading || !provider || !model || !prompt.trim()}
                data-testid="playground-run-button"
                className="flex-1 gap-2"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Play className="w-4 h-4" />
                )}
                {loading ? "Running..." : "Run"}
              </Button>
              <Button
                variant="outline"
                onClick={handleCopyRequest}
                disabled={!provider || !model}
                data-testid="playground-copy-request-button"
                className="gap-2"
              >
                {copiedRequest ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                cURL
              </Button>
            </div>
          </CardContent>
        </Card>

      </div>
    </div>
  );
}
